import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform";
import {
  ClickAwayListener,
  cx,
  useAutoResizedTextarea,
  useTranslations,
} from "@jugl-web/utils";
import { FC, useEffect, useRef, useState, ChangeEvent } from "react";
import { TITLE_MAX_LENGTH } from "../../consts";
import { taskNameValidator } from "../../validators";

interface TitleBoxProps {
  title: string;
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  isEditable?: boolean;
  classes?: {
    titleText?: string;
    editingBox?: string;
  };
  onChange?: (title: string) => void;
  onInternalValueChange?: (title: string) => void;
}

export const TitleBox: FC<TitleBoxProps> = ({
  title,
  textareaRef: forwardedTextareaRef,
  isEditable = true,
  classes,
  onChange,
  onInternalValueChange,
}) => {
  const hasTitle = !!title;

  const [internalTitle, setInternalTitle] = useState("");
  const [isEditing, setIsEditing] = useState(!hasTitle);
  const { t } = useTranslations();

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { textareaProps, resize } = useAutoResizedTextarea(textareaRef);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;

    setInternalTitle(value);
    onInternalValueChange?.(value);
  };

  const handleClickAway = () => {
    if (!taskNameValidator(internalTitle)) {
      return;
    }

    if (internalTitle !== title) {
      onChange?.(internalTitle);
    }

    setIsEditing(false);
  };

  useEffect(() => {
    if (!hasTitle) {
      setIsEditing(true);
    }
  }, [hasTitle]);

  useEffect(() => {
    if (isEditing) {
      setInternalTitle(title);
      window.setTimeout(resize, 0);
    }
  }, [title, isEditing, resize]);

  if (isEditable && (isEditing || !hasTitle)) {
    return (
      <ClickAwayListener
        triggerEvent={{ mouse: "click" }}
        onClickAway={handleClickAway}
      >
        <div
          className={cx(
            "bg-grey-100 min-h-[92px] overflow-y-auto rounded-lg p-4",
            classes?.editingBox
          )}
        >
          <textarea
            ref={(element) => {
              textareaRef.current = element;

              if (forwardedTextareaRef) {
                forwardedTextareaRef.current = element;
              }
            }}
            onFocus={(e) =>
              e.target.setSelectionRange(title.length, title.length)
            }
            autoFocus
            className="text-dark max-h-[300px] w-full resize-none border-none bg-transparent font-[Roboto] text-[22px] font-medium outline-none placeholder:text-[#BDBDBD]"
            placeholder={t({
              id: "form-controls.task-title.placeholder",
              defaultMessage: "Task title",
            })}
            maxLength={TITLE_MAX_LENGTH}
            rows={1}
            value={internalTitle}
            onChange={handleChange}
            {...textareaProps}
          />
          <div className="mt-4">
            <span className="leading-2 text-sm font-medium text-[#BDBDBD]">
              {internalTitle.length}/{TITLE_MAX_LENGTH}
            </span>
          </div>
        </div>
      </ClickAwayListener>
    );
  }

  return (
    <InteractiveContainer
      className={cx(
        "transition-color group rounded pr-4 duration-300",
        isEditable && "hover:bg-grey-100",
        classes?.titleText
      )}
      isDisabled={!isEditable}
      onClick={() => setIsEditing(true)}
    >
      <div
        className={cx(
          "overflow-hidden break-words transition-transform duration-300",
          isEditable && "group-hover:translate-x-2"
        )}
      >
        <span className="text-dark text-[22px] font-medium">
          {hasTitle
            ? title
            : t({
                id: "tasks-page.no-title",
                defaultMessage: "(no title)",
              })}
        </span>
      </div>
    </InteractiveContainer>
  );
};
